import { httpRequest } from '@/utils/axios';
import { getFirstPageAdpParam } from './marketingPageAdp';
import type {
  MarketingBrandProductListResponse,
  MarketingCouponsReceiveResponse,
  MarketingCouponsResponse,
  MarketingFlashSaleProductListResponse,
  MarketingProductListResponse,
  MarketingRankingProductListResponse,
  MarketingSceneProductListResponse,
  MarketingPageTemplateType,
  RawMarketingRuntimeConfigData,
  SeedingItemsResponse,
  SeedingItemDetailResponse,
} from '@/app/(activity)/campaigns/[slug]/types';

const RUNTIME_CONFIG_URL = 'GET:/api/marketing_pages/runtime_config' as const;
const SEEDING_ITEMS_URL = 'GET:/api/marketing_pages/seeding/items' as const;
const SEEDING_GROUP_ITEMS_URL = 'GET:/api/marketing_pages/seeding/group_items' as const;
const SEEDING_ITEM_DETAIL_URL = 'GET:/api/marketing_pages/seeding/items' as const;
const MARKETING_PRODUCT_LIST_URL = 'GET:/api/marketing_pages/product_list' as const;
const MARKETING_PAGE_SUBSCRIPTIONS_URL = 'POST:/api/marketing_pages/subscriptions' as const;
const MARKETING_PAGE_UNSUBSCRIBE_URL =
  'POST:/api/marketing_pages/subscriptions/unsubscribe' as const;
const MARKETING_COUPONS_URL = 'GET:/api/marketing_pages/coupons' as const;
const MARKETING_COUPONS_RECEIVE_URL = 'POST:/api/marketing_pages/coupons/receive' as const;

/** 获取营销页面运行时配置（客户端） */
export const fetchMarketingRuntimeConfig = (
  slug: string,
): Promise<RawMarketingRuntimeConfigData> => {
  return httpRequest(RUNTIME_CONFIG_URL as any, {
    params: { slug },
    data: {},
  }).then((res) => (res as any).data || res);
};

interface MarketingCouponsParams {
  marketingPageId: number;
  isPreview?: boolean;
}

interface ReceiveMarketingCouponsParams {
  marketingPageId: number;
  couponIds: Array<number | string>;
}

export interface MarketingPageSubscriptionParams {
  email: string;
  activity_type: MarketingPageTemplateType;
  subscribed_periodical_date: string;
  marketing_page_id: number;
}

interface MarketingPageSubscriptionData {
  subscribed: boolean;
  already_subscribed: boolean;
  activity_type: MarketingPageTemplateType;
  subscribed_periodical_date: string;
  marketing_page_id: number | string;
}

export interface MarketingPageSubscriptionResult extends MarketingPageSubscriptionData {
  msg: string;
}

interface MarketingPageSubscriptionResponse {
  code?: number;
  msg?: string;
  data?: MarketingPageSubscriptionData;
}

export interface MarketingPageUnsubscribeParams {
  email: string;
  activity_type: MarketingPageTemplateType;
  marketing_page_id: number;
}

interface MarketingPageUnsubscribeData {
  subscribed: boolean;
  already_unsubscribed: boolean;
  activity_type: MarketingPageTemplateType;
  unsubscribed_at: string;
  marketing_page_id: number | string;
}

export interface MarketingPageUnsubscribeResult extends MarketingPageUnsubscribeData {
  msg: string;
}

interface MarketingPageUnsubscribeResponse {
  code?: number;
  msg?: string;
  data?: MarketingPageUnsubscribeData;
}

export const subscribeMarketingPage = (
  data: MarketingPageSubscriptionParams,
): Promise<MarketingPageSubscriptionResult> => {
  return httpRequest(MARKETING_PAGE_SUBSCRIPTIONS_URL as any, { data } as any, {
    success: false,
    fail: false,
  }).then((response) => {
    const result = response as unknown as MarketingPageSubscriptionResponse;

    if (result.code !== undefined && result.code !== 200) {
      throw new Error(result.msg || 'Marketing page subscription failed');
    }

    const subscription = result.data || (response as unknown as MarketingPageSubscriptionData);

    return {
      ...subscription,
      msg: result.msg || '',
    };
  });
};

export const unsubscribeMarketingPage = (
  data: MarketingPageUnsubscribeParams,
): Promise<MarketingPageUnsubscribeResult> => {
  return httpRequest(MARKETING_PAGE_UNSUBSCRIBE_URL as any, { data } as any, {
    success: false,
    fail: false,
  }).then((response) => {
    const result = response as unknown as MarketingPageUnsubscribeResponse;

    if (result.code !== undefined && result.code !== 200) {
      throw new Error(result.msg || 'Marketing page unsubscription failed');
    }

    const subscription = result.data || (response as unknown as MarketingPageUnsubscribeData);

    return {
      ...subscription,
      msg: result.msg || '',
    };
  });
};

export const fetchMarketingCoupons = ({
  marketingPageId,
  isPreview = false,
}: MarketingCouponsParams): Promise<MarketingCouponsResponse> => {
  return httpRequest(MARKETING_COUPONS_URL as any, {
    params: {
      marketing_page_id: marketingPageId,
      ...(isPreview ? { preview: 1 } : {}),
    },
    data: {},
  }).then((res) => (res as any).data || res);
};

export const receiveMarketingCoupons = ({
  marketingPageId,
  couponIds,
}: ReceiveMarketingCouponsParams): Promise<MarketingCouponsReceiveResponse> => {
  const couponPayload =
    couponIds.length === 1 ? { coupon_id: couponIds[0] } : { coupon_ids: couponIds };

  return httpRequest(MARKETING_COUPONS_RECEIVE_URL as any, {
    data: {
      marketing_page_id: marketingPageId,
      ...couponPayload,
    },
  }).then((res) => (res as any).data || res);
};

/**
 * 获取种草坑位列表（客户端）
 * @param marketingPageId 页面ID
 * @param isPreview 是否预览模式
 * @param page 页码
 * @param pageSize 每页条数
 */
export const fetchSeedingItems = (
  marketingPageId: number,
  isPreview: boolean = false,
  page: number = 1,
  pageSize: number = 20,
  adp?: string,
): Promise<SeedingItemsResponse> => {
  return httpRequest(SEEDING_ITEMS_URL as any, {
    params: {
      marketing_page_id: marketingPageId,
      page,
      page_size: pageSize,
      ...(isPreview ? { preview: 1 } : {}),
      ...getFirstPageAdpParam(page, adp),
    },
    data: {},
  }).then((res) => (res as any).data || res);
};

/** 按商品分组获取种草坑位列表（客户端） */
export const fetchSeedingGroupItems = (
  marketingPageId: number,
  productGroupId: number,
  isPreview: boolean = false,
  page: number = 1,
  pageSize: number = 20,
): Promise<SeedingItemsResponse> => {
  return httpRequest(SEEDING_GROUP_ITEMS_URL as any, {
    params: {
      marketing_page_id: marketingPageId,
      product_group_id: productGroupId,
      page,
      page_size: pageSize,
      ...(isPreview ? { preview: 1 } : {}),
    },
    data: {},
  }).then((res) => (res as any).data || res);
};

/**
 * 获取种草坑位详情（客户端）
 * @param itemId 坑位ID
 * @param marketingPageId 页面ID
 * @param isPreview 是否预览模式
 */
export const fetchSeedingItemDetail = (
  itemId: number,
  marketingPageId: number,
  isPreview: boolean = false,
): Promise<SeedingItemDetailResponse> => {
  return httpRequest(`${SEEDING_ITEM_DETAIL_URL}/${itemId}` as any, {
    params: {
      marketing_page_id: marketingPageId,
      ...(isPreview ? { preview: 1 } : {}),
    },
    data: {},
  }).then((res) => (res as any).data || res);
};

interface MarketingProductListParams {
  appendCorrelation?: 0 | 1;
  groupId: number;
  page?: number;
  pageSize?: number;
  pageId: number;
  isPreview?: boolean;
  adp?: string;
  in_stock?: number;
  sold_out_sort?: number;
}

interface MarketingFlashSaleProductListParams {
  newUserRefId: number;
  oldUserRefId: number;
  page?: number;
  pageSize?: number;
  pageId: number;
  isPreview?: boolean;
  adp?: string;
}

interface MarketingBrandProductListParams {
  marketingPageId: number;
  isPreview?: boolean;
  page?: number;
  pageSize?: number;
  adp?: string;
}

interface MarketingSceneProductListParams {
  marketingPageId: number;
  isPreview?: boolean;
  itemIndex: number;
  page?: number;
  pageSize?: number;
  adp?: string;
}

interface MarketingRankingProductListParams {
  marketingPageId: number;
  isPreview?: boolean;
  itemIndex: number;
  page?: number;
  pageSize?: number;
}

interface MarketingStyleCategoryProductListParams {
  pageId: number;
  itemIndex: number;
  page?: number;
  pageSize?: number;
  adp?: string;
  isPreview?: boolean;
  appendCorrelation?: number;
}

export const fetchMarketingProductList = ({
  appendCorrelation,
  groupId,
  page = 1,
  pageSize = 20,
  pageId,
  isPreview = false,
  adp,
  in_stock,
  sold_out_sort,
}: MarketingProductListParams): Promise<MarketingProductListResponse> => {
  return httpRequest(MARKETING_PRODUCT_LIST_URL as any, {
    params: {
      type: 'product_group',
      ref_id: groupId,
      page,
      page_size: pageSize,
      marketing_page_id: pageId,
      ...(appendCorrelation !== undefined ? { append_correlation: appendCorrelation } : {}),
      ...(in_stock !== undefined ? { in_stock } : {}),
      ...(sold_out_sort !== undefined ? { sold_out_sort } : {}),
      ...(isPreview ? { preview: 1 } : {}),
      ...getFirstPageAdpParam(page, adp),
    },
    data: {},
  }).then((res) => (res as any).data || res);
};
// 风格爆品模块-卡片切换获取对应商品列表
export const fetchMarketingStyleCategoryProductList = ({
  pageId,
  itemIndex,
  page = 1,
  pageSize = 50,
  adp,
  isPreview = false,
  appendCorrelation = 1,
}: MarketingStyleCategoryProductListParams): Promise<MarketingProductListResponse> => {
  return httpRequest(
    MARKETING_PRODUCT_LIST_URL as any,
    {
      params: {
        type: 'style_category_product',
        marketing_page_id: pageId,
        item_index: itemIndex,
        page,
        page_size: pageSize,
        append_correlation: appendCorrelation,
        ...(isPreview ? { preview: 1 } : {}),
        ...getFirstPageAdpParam(page, adp),
      },
      data: {},
    },
    { success: false, fail: false },
  ).then((res) => (res as any).data || res);
};

export const fetchMarketingBrandProductList = ({
  marketingPageId,
  isPreview = false,
  page = 1,
  pageSize = 8,
  adp,
}: MarketingBrandProductListParams): Promise<MarketingBrandProductListResponse> => {
  return httpRequest(MARKETING_PRODUCT_LIST_URL as any, {
    params: {
      type: 'brand_product',
      marketing_page_id: marketingPageId,
      page,
      page_size: pageSize,
      append_correlation: 1,
      ...(isPreview ? { preview: 1 } : {}),
      ...getFirstPageAdpParam(page, adp),
    },
    data: {},
  }).then((res) => (res as any).data || res);
};

export const fetchMarketingSceneProductList = ({
  marketingPageId,
  isPreview = false,
  itemIndex,
  page = 1,
  pageSize = 20,
  adp,
}: MarketingSceneProductListParams): Promise<MarketingSceneProductListResponse> => {
  return httpRequest(MARKETING_PRODUCT_LIST_URL as any, {
    params: {
      type: 'scene_product',
      marketing_page_id: marketingPageId,
      item_index: itemIndex,
      page,
      page_size: pageSize,
      ...(isPreview ? { preview: 1 } : {}),
      ...getFirstPageAdpParam(page, adp),
    },
    data: {},
  }).then((res) => (res as any).data || res);
};

export const fetchMarketingRankingProductList = ({
  marketingPageId,
  isPreview = false,
  itemIndex,
  page = 1,
  pageSize = 18,
}: MarketingRankingProductListParams): Promise<MarketingRankingProductListResponse> => {
  return httpRequest(MARKETING_PRODUCT_LIST_URL as any, {
    params: {
      type: 'ranking_product',
      marketing_page_id: marketingPageId,
      item_index: itemIndex,
      page,
      page_size: pageSize,
      ...(isPreview ? { preview: 1 } : {}),
    },
    data: {},
  }).then((res) => (res as any).data || res);
};

export const fetchMarketingFlashSaleProductList = ({
  newUserRefId,
  oldUserRefId,
  page = 1,
  pageSize = 8,
  pageId,
  isPreview = false,
  adp,
}: MarketingFlashSaleProductListParams): Promise<MarketingProductListResponse> => {
  return httpRequest(MARKETING_PRODUCT_LIST_URL as any, {
    params: {
      type: 'flash_sale',
      new_user_ref_id: newUserRefId,
      old_user_ref_id: oldUserRefId,
      page,
      page_size: pageSize,
      marketing_page_id: pageId,
      ...(isPreview ? { preview: 1 } : {}),
      ...getFirstPageAdpParam(page, adp),
    },
    data: {},
  }).then((res) => (res as any).data || res);
};

export const fetchMarketingMainFlashSaleProductList = (
  marketingPageId: number,
  isPreview: boolean = false,
  adp?: string,
): Promise<MarketingFlashSaleProductListResponse> => {
  return httpRequest(MARKETING_PRODUCT_LIST_URL as any, {
    params: {
      type: 'flash_sale',
      marketing_page_id: marketingPageId,
      ...(isPreview ? { preview: 1 } : {}),
      ...(adp ? { adp } : {}),
    },
    data: {},
  }).then((res) => (res as any).data || res);
};
